<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Usuarios
      </h1>
      <p class="text-gray-600 dark:text-gray-400">
        Administra los usuarios registrados en el sistema
      </p>
    </div>
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            Lista de Usuarios
          </h2>
          <div class="flex gap-2">
            <button @click="goToNegocios" class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 7h10M7 11h10M7 15h10"/>
              </svg>
              Gestionar Negocios
            </button>
            <button @click="showModal = true" class="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              Nuevo Usuario
            </button>
          </div>
        </div>
        <!-- Filtros -->
        <form @submit.prevent="onFilter" class="flex flex-wrap gap-2 mt-4">
          <input v-model="filters.username" type="text" placeholder="Username" class="px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          <input v-model="filters.email" type="text" placeholder="Email" class="px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          <input v-model="filters.nombre" type="text" placeholder="Nombre o Apellido" class="px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          <button type="submit" class="px-4 py-2 rounded bg-blue-500 text-white text-sm font-semibold hover:bg-blue-600 transition">Buscar</button>
          <button type="button" @click="onClearFilters" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition">Limpiar</button>
        </form>
      </div>
      <div class="p-6">
        <div v-if="error" class="text-red-500 text-center mb-4">{{ error }}</div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
              <tr>
                <th class="px-4 py-2 font-bold">ID</th>
                <th class="px-4 py-2 font-bold">Username</th>
                <th class="px-4 py-2 font-bold">Email</th>
                <th class="px-4 py-2 font-bold">Rol</th>
                <th class="px-4 py-2 font-bold">Nombre</th>
                <th class="px-4 py-2 font-bold">Apellido Paterno</th>
                <th class="px-4 py-2 font-bold">Apellido Materno</th>
                <th class="px-4 py-2 font-bold">Teléfono</th>
                <th class="px-4 py-2 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in users" :key="user.id" class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 even:bg-gray-50 dark:even:bg-gray-700">
                <td class="px-4 py-2 font-medium text-gray-900 dark:text-white">{{ user.id }}</td>
                <td class="px-4 py-2 text-gray-700 dark:text-gray-200">{{ user.username }}</td>
                <td class="px-4 py-2 text-gray-700 dark:text-gray-200">{{ user.email }}</td>
                <td class="px-4 py-2">
                  <RolUsuario 
                    :rol-id="user.rol?.id || 0"
                    :nombre="user.rol?.nombre || 'Sin rol'"
                  />
                </td>
                <td class="px-4 py-2 text-gray-700 dark:text-gray-200">{{ user.nombre }}</td>
                <td class="px-4 py-2 text-gray-700 dark:text-gray-200">{{ user.apellidoPaterno }}</td>
                <td class="px-4 py-2 text-gray-700 dark:text-gray-200">{{ user.apellidoMaterno }}</td>
                <td class="px-4 py-2 text-gray-700 dark:text-gray-200">{{ user.telefono }}</td>
                <td class="px-4 py-2 whitespace-nowrap text-right">
                  <button @click="openEditModal(user)" class="text-blue-500 hover:text-blue-700 mr-2" title="Editar">
                    <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536M9 13h3l8-8a2.828 2.828 0 00-4-4l-8 8v3z"/>
                    </svg>
                  </button>
                  <button @click="onDeleteUser(user)" class="text-red-500 hover:text-red-700" title="Eliminar">
                    <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </td>
              </tr>
              <tr v-if="users.length === 0">
                <td colspan="9" class="text-center py-4 text-gray-400">No hay usuarios para mostrar.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Paginador -->
        <div v-if="lastPage > 1" class="flex justify-center mt-4 gap-2">
          <button :disabled="currentPage === 1" @click="goToPage(currentPage - 1)" class="px-3 py-1 rounded border text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed">
            Anterior
          </button>
          <button v-for="page in pages" :key="page" @click="goToPage(page)" :class="['px-3 py-1 rounded border text-sm font-semibold transition', page === currentPage ? 'bg-blue-500 text-white border-blue-500' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-700']">
            {{ page }}
          </button>
          <button :disabled="currentPage === lastPage" @click="goToPage(currentPage + 1)" class="px-3 py-1 rounded border text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed">
            Siguiente
          </button>
        </div>
      </div>
    </div>

    <!-- Modal para Nuevo Usuario -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              Nuevo Usuario
            </h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        
        <form @submit.prevent="createUser" class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Username *
            </label>
            <input 
              v-model="newUser.username" 
              type="text" 
              required
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
              placeholder="Ingrese el username"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email *
            </label>
            <input 
              v-model="newUser.email" 
              type="email" 
              required
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
              placeholder="Ingrese el email"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Contraseña *
            </label>
            <input 
              v-model="newUser.password" 
              type="password" 
              required
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
              placeholder="Ingrese la contraseña"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Rol *
            </label>
            <select 
              v-model="newUser.rol_id" 
              required
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Seleccione un rol</option>
              <option v-for="role in roles" :key="role.id" :value="role.id">
                {{ role.nombre }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nombre *
            </label>
            <input 
              v-model="newUser.nombre" 
              type="text" 
              required
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
              placeholder="Ingrese el nombre"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Apellido Paterno *
            </label>
            <input 
              v-model="newUser.apellidoPaterno" 
              type="text" 
              required
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
              placeholder="Ingrese el apellido paterno"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Apellido Materno
            </label>
            <input 
              v-model="newUser.apellidoMaterno" 
              type="text" 
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
              placeholder="Ingrese el apellido materno"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Teléfono
            </label>
            <input 
              v-model="newUser.telefono" 
              type="tel" 
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
              placeholder="Ingrese el teléfono"
            />
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button 
              type="button" 
              @click="closeModal"
              class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              :disabled="loading"
              class="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <svg v-if="loading" class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              {{ loading ? 'Creando...' : 'Crear Usuario' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal para Editar Usuario -->
    <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              Editar Usuario
            </h3>
            <button @click="closeEditModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        <form @submit.prevent="updateUser" class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username</label>
            <input v-model="editUser.username" type="text" disabled class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-400" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input v-model="editUser.email" type="email" disabled class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-400" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rol *</label>
            <select v-model="editUser.rol_id" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white">
              <option value="">Seleccione un rol</option>
              <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.nombre }}</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre *</label>
            <input v-model="editUser.nombre" type="text" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Apellido Paterno *</label>
            <input v-model="editUser.apellidoPaterno" type="text" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Apellido Materno</label>
            <input v-model="editUser.apellidoMaterno" type="text" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teléfono</label>
            <input v-model="editUser.telefono" type="tel" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white" />
          </div>
          <div class="flex justify-end space-x-3 pt-4">
            <button type="button" @click="closeEditModal" class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">Cancelar</button>
            <button type="submit" :disabled="loading" class="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
              <svg v-if="loading" class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              {{ loading ? 'Actualizando...' : 'Actualizar Usuario' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watchEffect } from 'vue'
import { useRouter } from 'vue-router'
import { useHttp } from '@/composables/useHttp'
import RolUsuario from '@/components/RolUsuario.vue'
import Swal from 'sweetalert2'

const router = useRouter()

const users = ref([])
const currentPage = ref(1)
const lastPage = ref(1)
const pages = ref([])
const perPage = 10
const showModal = ref(false)
const showEditModal = ref(false)
const editUser = ref({})
const { get, post, loading, error, data, del } = useHttp()

// Formulario para nuevo usuario
const newUser = ref({
  username: '',
  email: '',
  password: '',
  rol_id: '',
  nombre: '',
  apellidoPaterno: '',
  apellidoMaterno: '',
  telefono: ''
})

const roles = ref([])
const filters = ref({ username: '', email: '', nombre: '' })

const fetchUsers = async (page = 1) => {
  // console.log("Iniciando fetchUsers...");
  
  try {
    // Construye query params con filtros
    const params = new URLSearchParams()
    params.append('page', page)
    params.append('per_page', perPage)
    if (filters.value.username) params.append('username', filters.value.username)
    if (filters.value.email) params.append('email', filters.value.email)
    if (filters.value.nombre) params.append('nombre', filters.value.nombre)
    await get(`/users?${params.toString()}`)
    // console.log("Respuesta de usuarios:", data.value);
    if (data.value) {
      users.value = data.value.data
      currentPage.value = data.value.current_page
      lastPage.value = data.value.last_page
      // Genera el array de páginas para el paginador
      pages.value = []
      for (let i = 1; i <= lastPage.value; i++) {
        pages.value.push(i)
      }
      // console.log("Datos procesados correctamente");
      return; // Si funciona, salimos aquí
    }
  } catch (err) {
    console.error("Error en usuarios:", err);
  }
  
  // Datos de prueba temporal (solo si falla la API)
  // users.value = [
  //   {
  //     id: 1,
  //     username: 'nerk',
  //     email: 'nerk@example.com',
  //     rol_id: 1,
  //     nombre: 'Juan',
  //     apellidoPaterno: 'Urrutia',
  //     apellidoMaterno: 'Ramírez',
  //     telefono: '4777043987',
  //     created_at: '2025-07-14T06:10:32.000000Z',
  //     updated_at: '2025-07-21T00:10:50.000000Z'
  //   }
  // ];
  // currentPage.value = 1;
  // lastPage.value = 1;
  // pages.value = [1];
  // console.log("Datos de prueba cargados");
}

const fetchRoles = async () => {
  try {
    // console.log("Cargando roles...");
    await get('/roles')
    if (data.value) {
      roles.value = data.value
      // console.log("Roles cargados:", roles.value);
    }
  } catch (err) {
    console.error("Error al cargar roles:", err);
    // Datos de prueba para roles si falla la API
    roles.value = [
      { id: 1, nombre: 'Super Admin' },
      { id: 2, nombre: 'Admin' },
      { id: 3, nombre: 'Rol 1' },
      { id: 4, nombre: 'Rol 2' },
      { id: 5, nombre: 'Rol 3' }
    ];
  }
}

const goToPage = (page) => {
  if (page >= 1 && page <= lastPage.value) {
    fetchUsers(page)
  }
}

const closeModal = () => {
  showModal.value = false
  // Limpiar formulario
  newUser.value = {
    username: '',
    email: '',
    password: '',
    rol_id: '',
    nombre: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    telefono: ''
  }
}

const createUser = async () => {
  try {
    // console.log("Creando usuario:", newUser.value)
    await post('/users', newUser.value)
    // console.log("Usuario creado:", data.value)
    
    // Cerrar modal y recargar lista
    closeModal()
    await fetchUsers(currentPage.value)
    
    // Notificación de éxito con SweetAlert2
    Swal.fire({
      icon: 'success',
      title: 'Usuario creado',
      text: 'El usuario se creó exitosamente.',
      timer: 2000,
      showConfirmButton: false
    })
  } catch (err) {
    console.error("Error al crear usuario:", err)
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.value || 'Error desconocido al crear usuario'
    })
  }
}

function openEditModal(user) {
  editUser.value = { ...user }
  showEditModal.value = true
}
function closeEditModal() {
  showEditModal.value = false
  editUser.value = {}
}
const updateUser = async () => {
  try {
    await post(`/users/${editUser.value.id}?_method=PUT`, editUser.value) // O usa put si tu backend lo permite
    closeEditModal()
    await fetchUsers(currentPage.value)
    Swal.fire({
      icon: 'success',
      title: 'Usuario actualizado',
      text: 'El usuario se actualizó exitosamente.',
      timer: 2000,
      showConfirmButton: false
    })
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.value || 'Error desconocido al actualizar usuario'
    })
  }
}

const onDeleteUser = async (user) => {
  const result = await Swal.fire({
    title: `¿Eliminar usuario?`,
    text: `¿Seguro que deseas eliminar a ${user.username}? Esta acción no se puede deshacer.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar',
  })
  if (result.isConfirmed) {
    try {
      await del(`/users/${user.id}`)
      await Swal.fire({
        title: '¡Eliminado!',
        text: 'El usuario ha sido eliminado correctamente.',
        icon: 'success',
        timer: 1800,
        showConfirmButton: false
      })
      fetchUsers(currentPage.value)
    } catch (err) {
      Swal.fire('Error', 'No se pudo eliminar el usuario.', 'error')
    }
  }
}

const onFilter = () => {
  fetchUsers(1)
}
const onClearFilters = () => {
  filters.value = { username: '', email: '', nombre: '' }
  fetchUsers(1)
}

const goToNegocios = () => {
  router.push('/admin/negocios')
}


// Carga inicial
watchEffect(() => {
  fetchUsers(currentPage.value)
  fetchRoles()
})
</script> 